import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getDomainInfo } from '@/api/domains'
import { IdentityInfo } from '@/lib/types'
import { DomainCard } from '@/components/domains/domain-card'
import { DeleteDomainDialog } from '@/components/domains/delete-dialog'
import { VerificationStatus } from '@/components/domains/verification-status'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, Trash2, Home, AlertTriangle } from 'lucide-react'

export function DomainDetailPage() {
  const { domain: encodedDomain } = useParams<{ domain: string }>()
  const domain = encodedDomain ? decodeURIComponent(encodedDomain) : ''
  const navigate = useNavigate()
  const [domainInfo, setDomainInfo] = useState<IdentityInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const fetchDomain = useCallback(async (showLoading = true) => {
    if (!domain) return

    if (showLoading) {
      setLoading(true)
    }
    const result = await getDomainInfo(domain)
    if (result.success && result.data) {
      setDomainInfo(result.data)
      setError(null)
    } else {
      setError(result.error || 'Failed to fetch domain info')
    }
    setLoading(false)
  }, [domain])

  useEffect(() => {
    fetchDomain()
  }, [fetchDomain])

  const handleStatusChange = useCallback(() => {
    fetchDomain(false)
  }, [fetchDomain])

  const handleDeleteSuccess = () => {
    setDeleteDialogOpen(false)
    navigate('/dashboard/domains')
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (error || !domainInfo) {
    const isNotFound = error?.toLowerCase().includes('not found') || !domainInfo

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <div className="text-center space-y-6 max-w-md">
          {isNotFound ? (
            <>
              <div className="text-8xl font-bold text-muted-foreground/30">404</div>
              <h1 className="text-2xl font-semibold">Domain not found</h1>
              <p className="text-muted-foreground">
                The domain <span className="font-mono font-medium">{domain}</span> doesn't exist in our registry.
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center">
                <AlertTriangle className="h-16 w-16 text-destructive/70" />
              </div>
              <h1 className="text-2xl font-semibold">Something went wrong</h1>
              <p className="text-muted-foreground">{error}</p>
            </>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go back
            </Button>
            <Link to="/dashboard/domains">
              <Button>
                <Home className="mr-2 h-4 w-4" />
                All Domains
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/domains">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">{domain}</h1>
        </div>
        <Button
          variant="destructive"
          onClick={() => setDeleteDialogOpen(true)}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete Domain
        </Button>
      </div>

      <DomainCard domain={domainInfo} />

      <VerificationStatus
        accountId={domainInfo.account_id}
        domain={domain}
        onStatusChange={handleStatusChange}
      />

      <DeleteDomainDialog
        domain={domain}
        accountId={domainInfo.account_id}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  )
}
